import React, { useState, useEffect } from 'react';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import offensiveApi from '../../services/offensiveApi';

function OffensiveKnowledgeBase() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('mitre'); // 'mitre', 'tools'

  // MITRE ATT&CK State (Tactics + Techniques + Subtechniques)
  const [tactics, setTactics] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [subtechniquesMap, setSubtechniquesMap] = useState({});
  const [techniquesByTactic, setTechniquesByTactic] = useState({});
  const [mitreLoading, setMitreLoading] = useState(true);
  const [expandedTactics, setExpandedTactics] = useState(new Set());
  const [expandedTechniques, setExpandedTechniques] = useState(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set()); // For read more/less
  const [mitreSearchTerm, setMitreSearchTerm] = useState('');

  // Villager Exploits State
  const [exploits, setExploits] = useState([]);
  const [exploitsLoading, setExploitsLoading] = useState(false);
  const [exploitsPage, setExploitsPage] = useState(1);
  const [exploitsStats, setExploitsStats] = useState(null);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [exploitSearchTerm, setExploitSearchTerm] = useState('');

  // Fetch MITRE ATT&CK Data (Tactics, Techniques, Subtechniques)
  useEffect(() => {
    const fetchMitreData = async () => {
      try {
        setMitreLoading(true);
        
        // Fetch tactics
        const tacticsData = await offensiveApi.getMitreTactics();
        
        // Define the proper MITRE ATT&CK kill chain order
        const tacticOrder = {
          'TA0043': 1,  // Reconnaissance
          'TA0042': 2,  // Resource Development
          'TA0001': 3,  // Initial Access
          'TA0002': 4,  // Execution
          'TA0003': 5,  // Persistence
          'TA0004': 6,  // Privilege Escalation
          'TA0005': 7,  // Defense Evasion
          'TA0006': 8,  // Credential Access
          'TA0007': 9,  // Discovery
          'TA0008': 10, // Lateral Movement
          'TA0009': 11, // Collection
          'TA0011': 12, // Command and Control
          'TA0010': 13, // Exfiltration
          'TA0040': 14, // Impact
        };
        
        // Sort tactics by kill chain order
        const sortedTactics = (tacticsData || []).sort((a, b) => {
          const orderA = tacticOrder[a.id] || 999;
          const orderB = tacticOrder[b.id] || 999;
          return orderA - orderB;
        });
        
        setTactics(sortedTactics);
        
        // Create a mapping from tactic name (lowercase, hyphenated) to tactic ID
        // The backend returns tactic names like "privilege-escalation" but tactics have IDs like "TA0004"
        const tacticNameToId = {};
        (tacticsData || []).forEach(tactic => {
          // Map the name (lowercase, hyphenated) to the ID
          const nameKey = tactic.name?.toLowerCase().replace(/\s+/g, '-') || '';
          tacticNameToId[nameKey] = tactic.id;
          // Also map ID to itself in case techniques use IDs
          tacticNameToId[tactic.id] = tactic.id;
          // Also map the external_id if it exists (e.g., from MITRE URL)
          if (tactic.external_id) {
            const urlParts = tactic.external_id.split('/');
            const urlName = urlParts[urlParts.length - 1]?.toLowerCase();
            if (urlName) {
              tacticNameToId[urlName] = tactic.id;
            }
          }
        });
        
        // Fetch all techniques
        const techniquesData = await offensiveApi.getMitreTechniques();
        setTechniques(techniquesData || []);
        
        // Group techniques by tactic ID
        const groupedByTactic = {};
        (techniquesData || []).forEach(technique => {
          if (technique.tactics && technique.tactics.length > 0) {
            technique.tactics.forEach(tacticRef => {
              // tacticRef could be a tactic ID (TA0004) or a tactic name (privilege-escalation)
              // Try to find the tactic ID
              let tacticId = tacticNameToId[tacticRef];
              if (!tacticId) {
                // Try lowercase version
                tacticId = tacticNameToId[tacticRef.toLowerCase()];
              }
              if (!tacticId) {
                // If still not found, use the ref as-is (might already be an ID)
                tacticId = tacticRef;
              }
              
              if (!groupedByTactic[tacticId]) {
                groupedByTactic[tacticId] = [];
              }
            groupedByTactic[tacticId].push(technique);
          });
        }
      });
      setTechniquesByTactic(groupedByTactic);
        
        // Fetch ALL subtechniques at once (more efficient than per-technique)
        try {
          const allSubtechniques = await offensiveApi.getMitreSubtechniques();
          
          // Group subtechniques by parent technique
          const map = {};
          // Initialize all techniques with empty arrays
          (techniquesData || []).forEach(technique => {
            map[technique.id] = [];
          });
          
          // Group subtechniques by their parent
          (allSubtechniques || []).forEach(subtechnique => {
            // Check various possible parent field names
            const parentId = subtechnique.parent_technique_id || 
                           subtechnique.parent_technique || 
                           subtechnique.parent_id ||
                           subtechnique.parent;
            
            if (parentId) {
              if (!map[parentId]) {
                map[parentId] = [];
              }
              map[parentId].push(subtechnique);
            } else {
              // If no parent field, try to extract from ID (e.g., T1003.001 -> T1003)
              const match = subtechnique.id?.match(/^(T\d+)\.\d+$/);
              if (match) {
                const parentId = match[1];
                if (!map[parentId]) {
                  map[parentId] = [];
                }
                map[parentId].push(subtechnique);
              }
            }
          });
          
          setSubtechniquesMap(map);
        } catch (error) {
          console.error('Error fetching subtechniques:', error);
          // Set empty map on error
          const emptyMap = {};
          (techniquesData || []).forEach(technique => {
            emptyMap[technique.id] = [];
          });
          setSubtechniquesMap(emptyMap);
        }
      } catch (error) {
        console.error('Error fetching MITRE data:', error);
      } finally {
        setMitreLoading(false);
      }
    };

    if (activeTab === 'mitre') {
      fetchMitreData();
    }
  }, [activeTab]);

  // Fetch Villager Exploits
  useEffect(() => {
    const fetchExploits = async () => {
      if (exploitSearchTerm.trim()) {
        // Search mode
        try {
          setExploitsLoading(true);
          const data = await offensiveApi.searchVillagerExploits(exploitSearchTerm, 50);
          setExploits(data || []);
        } catch (error) {
          console.error('Error searching exploits:', error);
        } finally {
          setExploitsLoading(false);
        }
      } else {
        // Normal pagination mode
        try {
          setExploitsLoading(true);
          const data = await offensiveApi.getVillagerExploits(exploitsPage, 50, selectedSeverity);
          setExploits(data || []);
        } catch (error) {
          console.error('Error fetching exploits:', error);
        } finally {
          setExploitsLoading(false);
        }
      }
    };

    if (activeTab === 'tools') {
      fetchExploits();
    }
  }, [activeTab, exploitsPage, selectedSeverity, exploitSearchTerm]);

  // Fetch Villager Exploits Stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const stats = await offensiveApi.getVillagerExploitsStats();
        setExploitsStats(stats);
      } catch (error) {
        console.error('Error fetching exploit stats:', error);
      }
    };

    if (activeTab === 'tools') {
      fetchStats();
    }
  }, [activeTab]);

  // Toggle tactic expansion to show techniques
  const toggleTacticExpansion = (tacticId) => {
    setExpandedTactics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tacticId)) {
        newSet.delete(tacticId);
      } else {
        newSet.add(tacticId);
      }
      return newSet;
    });
  };

  // Toggle technique expansion to show subtechniques
  const toggleTechniqueExpansion = (techniqueId) => {
    setExpandedTechniques(prev => {
      const newSet = new Set(prev);
      if (newSet.has(techniqueId)) {
        newSet.delete(techniqueId);
      } else {
        newSet.add(techniqueId);
      }
      return newSet;
    });
  };

  // Parse description to get first sentence (short description) and rest
  const parseDescription = (description) => {
    if (!description) return { short: '', full: '' };
    
    // Get first sentence (up to first period, exclamation, or question mark)
    const firstSentenceMatch = description.match(/^[^.!?]+[.!?]/);
    const short = firstSentenceMatch ? firstSentenceMatch[0].trim() : description.split('\n')[0].trim();
    const full = description.trim();
    
    return { short, full };
  };

  // Truncate text to a certain length
  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Toggle description expansion
  const toggleDescription = (id) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Filter tactics and techniques by search term
  const getFilteredData = () => {
    if (!mitreSearchTerm.trim()) {
      return { tactics, techniquesByTactic };
    }
    
    const searchLower = mitreSearchTerm.toLowerCase();
    const filteredTactics = tactics.filter(tactic =>
      tactic.name?.toLowerCase().includes(searchLower) ||
      tactic.id?.toLowerCase().includes(searchLower) ||
      tactic.description?.toLowerCase().includes(searchLower)
    );
    
    const filteredTechniquesByTactic = {};
    Object.keys(techniquesByTactic).forEach(tacticId => {
      const filtered = techniquesByTactic[tacticId].filter(tech =>
        tech.name?.toLowerCase().includes(searchLower) ||
        tech.id?.toLowerCase().includes(searchLower) ||
        tech.description?.toLowerCase().includes(searchLower)
      );
      if (filtered.length > 0) {
        filteredTechniquesByTactic[tacticId] = filtered;
        // Auto-expand tactics that have matching techniques
        if (!expandedTactics.has(tacticId)) {
          setExpandedTactics(prev => new Set([...prev, tacticId]));
        }
      }
    });
    
    return { tactics: filteredTactics, techniquesByTactic: filteredTechniquesByTactic };
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/* Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            
            {/* Page header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    Offensive Knowledge Base
                  </h1>
                  <p className="text-slate-600 dark:text-slate-400 mt-2">
                    Explore MITRE ATT&CK tactics, techniques, subtechniques, and offensive tools
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6">
              <div className="border-b border-slate-200 dark:border-slate-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('mitre')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'mitre'
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      MITRE ATT&CK
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('tools')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'tools'
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Tools & Exploits
                    </div>
                  </button>
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'mitre' && (
              <div className="space-y-6">
                {/* Search Bar */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-4">
                  <input
                    type="text"
                    placeholder="Search MITRE ATT&CK"
                    value={mitreSearchTerm}
                    onChange={(e) => setMitreSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {mitreLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <>
                    {/* Tactics Horizontal Scrolling Container */}
                    <div className="mb-8">
                      <div className="overflow-x-auto overflow-y-visible pb-4 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent">
                        <div className="flex gap-6 min-w-max items-start">
                          {(() => {
                            const { tactics: filteredTactics, techniquesByTactic: filteredTechniquesByTactic } = getFilteredData();
                            
                            return filteredTactics.map((tactic) => {
                              const tacticTechniques = filteredTechniquesByTactic[tactic.id] || [];
                              const isTacticExpanded = expandedTactics.has(tactic.id);
                              const hasTechniques = tacticTechniques.length > 0;
                              const { short, full } = parseDescription(tactic.description);
                              const descId = `tactic-${tactic.id}`;
                              const isDescExpanded = expandedDescriptions.has(descId);
                              const shouldTruncate = full.length > 200;
                              const displayDesc = isDescExpanded || !shouldTruncate ? full : truncateText(full, 200);

                              return (
                                <div
                                  key={tactic.id}
                                  className="w-96 flex-shrink-0 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-xl transition-all"
                                >
                                  {/* Tactic Card */}
                                  <div className="p-6">
                                    {/* Tactic Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                      <div className="w-16 h-10 rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                                        <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400 font-mono">
                                          {tactic.id}
                                        </span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h3 className="text-md font-bold text-slate-800 dark:text-slate-100 mb-1">
                                          {tactic.name}
                                        </h3>
                                      </div>
                                    </div>
                                    
                                    {/* Short Description */}
                                    {short && (
                                      <p className="text-sm text-slate-700 dark:text-slate-300 mb-3 font-medium">
                                        {short}
                                      </p>
                                    )}
                                    
                                    {/* Full Description with Read More */}
                                    {full && full !== short && (
                                      <div className="mb-4">
                                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                          {displayDesc}
                                        </p>
                                        {shouldTruncate && (
                                          <button
                                            onClick={() => toggleDescription(descId)}
                                            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline mt-2 font-medium"
                                          >
                                            {isDescExpanded ? 'Read less' : 'Read more'}
                                          </button>
                                        )}
                                      </div>
                                    )}

                                    {/* Techniques Count & Expand Button */}
                                    {hasTechniques && (
                                      <button
                                        onClick={() => toggleTacticExpansion(tactic.id)}
                                        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors text-left border border-slate-200 dark:border-slate-700"
                                      >
                                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                          {tacticTechniques.length} technique{tacticTechniques.length !== 1 ? 's' : ''}
                                        </span>
                                        <svg
                                          className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isTacticExpanded ? 'rotate-180' : ''}`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                    )}

                                    {tactic.external_id && (
                                      <a
                                        href={tactic.external_id}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline mt-4 inline-block"
                                      >
                                        View on MITRE →
                                      </a>
                                    )}
                                  </div>

                                  {/* Techniques (Nested under Tactic) - Smooth Drawer Effect */}
                                  {isTacticExpanded && hasTechniques && (
                                    <div 
                                      className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 animate-slideDown"
                                      style={{
                                        maxHeight: '600px',
                                        overflowY: 'auto'
                                      }}
                                    >
                                      <div className="p-4 space-y-4">
                                        {tacticTechniques.map((technique) => {
                                          const subtechniques = subtechniquesMap[technique.id] || [];
                                          const isTechniqueExpanded = expandedTechniques.has(technique.id);
                                          const hasSubtechniques = subtechniques.length > 0;
                                          const techDescId = `technique-${technique.id}`;
                                          const isTechDescExpanded = expandedDescriptions.has(techDescId);
                                          const { short: techShort, full: techFull } = parseDescription(technique.description || '');
                                          const shouldTruncateTech = techFull.length > 200;
                                          const displayTechDesc = isTechDescExpanded || !shouldTruncateTech ? techFull : truncateText(techFull, 200);

                                          return (
                                            <div
                                              key={technique.id}
                                              className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
                                            >
                                              {/* Technique Card */}
                                              <div className="p-4">
                                                <div className="flex items-center gap-3 mb-3">
                                                  <span className="px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-sm font-mono flex-shrink-0">
                                                    {technique.id}
                                                  </span>
                                                  <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-1">
                                                      {technique.name}
                                                    </h4>
                                                    {hasSubtechniques && (
                                                      <span className="text-sm text-slate-500 dark:text-slate-400">
                                                        {subtechniques.length} subtechnique{subtechniques.length !== 1 ? 's' : ''}
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                                
                                                {/* Technique Description */}
                                                {techFull && (
                                                  <div className="mb-3">
                                                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                                      {displayTechDesc}
                                                    </p>
                                                    {shouldTruncateTech && (
                                                      <button
                                                        onClick={() => toggleDescription(techDescId)}
                                                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline mt-2 font-medium"
                                                      >
                                                        {isTechDescExpanded ? 'Read less' : 'Read more'}
                                                      </button>
                                                    )}
                                                  </div>
                                                )}

                                                {/* Platforms */}
                                                {(() => {
                                                  // Filter out "PRE" and other non-platform values
                                                  const validPlatforms = (technique.platforms || []).filter(
                                                    p => p && p.toLowerCase() !== 'pre' && p.toLowerCase() !== 'pre-attack'
                                                  );
                                                  
                                                  return validPlatforms.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mb-3">
                                                      {validPlatforms.slice(0, 4).map((platform, idx) => (
                                                        <span
                                                          key={idx}
                                                          className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-sm"
                                                        >
                                                          {platform}
                                                        </span>
                                                      ))}
                                                      {validPlatforms.length > 4 && (
                                                        <span className="px-2 py-1 text-slate-500 dark:text-slate-400 text-sm">
                                                          +{validPlatforms.length - 4}
                                                        </span>
                                                      )}
                                                    </div>
                                                  );
                                                })()}

                                                <div className="flex items-center justify-between gap-2">
                                                  {/* Expand Subtechniques Button */}
                                                  {hasSubtechniques && (
                                                    <button
                                                      onClick={() => toggleTechniqueExpansion(technique.id)}
                                                      className="flex-1 flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-750 transition-all duration-300 text-left border border-slate-200 dark:border-slate-700"
                                                    >
                                                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                        {isTechniqueExpanded ? 'Hide' : 'Show'} {subtechniques.length} subtechnique{subtechniques.length !== 1 ? 's' : ''}
                                                      </span>
                                                      <svg
                                                        className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isTechniqueExpanded ? 'rotate-180' : ''}`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                      >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                      </svg>
                                                    </button>
                                                  )}

                                                  {technique.external_id && (
                                                    <a
                                                      href={technique.external_id}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline flex-shrink-0"
                                                    >
                                                      MITRE →
                                                    </a>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Subtechniques - Slide Down Below Technique */}
                                              {isTechniqueExpanded && hasSubtechniques && (
                                                <div 
                                                  className="border-t border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 p-4 animate-slideDown"
                                                  style={{
                                                    maxHeight: '400px',
                                                    overflowY: 'auto'
                                                  }}
                                                >
                                                  <div className="space-y-3">
                                                    {subtechniques.map((subtechnique) => {
                                                      const subDescId = `subtechnique-${subtechnique.id}`;
                                                      const isSubDescExpanded = expandedDescriptions.has(subDescId);
                                                      const { short: subShort, full: subFull } = parseDescription(subtechnique.description || '');
                                                      const shouldTruncateSub = subFull.length > 200;
                                                      const displaySubDesc = isSubDescExpanded || !shouldTruncateSub ? subFull : truncateText(subFull, 200);

                                                      return (
                                                        <div
                                                          key={subtechnique.id}
                                                          className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 shadow-sm"
                                                        >
                                                          <div className="flex items-center gap-3 mb-2">
                                                            <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded text-sm font-mono flex-shrink-0">
                                                              {subtechnique.id}
                                                            </span>
                                                            <h5 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex-1">
                                                              {subtechnique.name}
                                                            </h5>
                                                          </div>
                                                          
                                                          {subFull && (
                                                            <div className="mb-2">
                                                              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                                                {displaySubDesc}
                                                              </p>
                                                              {shouldTruncateSub && (
                                                                <button
                                                                  onClick={() => toggleDescription(subDescId)}
                                                                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline mt-2 font-medium"
                                                                >
                                                                  {isSubDescExpanded ? 'Read less' : 'Read more'}
                                                                </button>
                                                              )}
                                                            </div>
                                                          )}

                                                          {subtechnique.external_id && (
                                                            <a
                                                              href={subtechnique.external_id}
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline inline-block"
                                                            >
                                                              View on MITRE →
                                                            </a>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>

                    {(() => {
                      const { tactics: filteredTactics } = getFilteredData();
                      return filteredTactics.length === 0 && (
                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-12 text-center">
                          <p className="text-slate-600 dark:text-slate-400">
                            {mitreSearchTerm.trim() ? 'No MITRE ATT&CK data found matching your search' : 'No MITRE ATT&CK data found'}
                          </p>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {activeTab === 'tools' && (
              <div className="space-y-6">
                {/* Stats Banner */}
                {exploitsStats && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                        Exploits & PoCs Database
                      </h2>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {exploitsStats.total?.toLocaleString() || 0} total entries
                      </span>
                    </div>
                    
                    {/* Severity Filter */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => {
                          setSelectedSeverity(null);
                          setExploitsPage(1);
                        }}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                          !selectedSeverity
                            ? 'bg-indigo-500 text-white'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                        }`}
                      >
                        All
                      </button>
                      {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => (
                        <button
                          key={sev}
                          onClick={() => {
                            setSelectedSeverity(sev);
                            setExploitsPage(1);
                          }}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            selectedSeverity === sev
                              ? 'bg-indigo-500 text-white'
                              : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                          }`}
                        >
                          {sev} ({exploitsStats.by_severity?.[sev] || 0})
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Bar */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-4">
                  <input
                    type="text"
                    placeholder="Search exploits (e.g., 'SQL injection', 'RCE', 'VMware')..."
                    value={exploitSearchTerm}
                    onChange={(e) => {
                      setExploitSearchTerm(e.target.value);
                      setExploitsPage(1);
                    }}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Exploits List */}
                {exploitsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : exploits.length === 0 ? (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-12 text-center">
                    <p className="text-slate-600 dark:text-slate-400">
                      {exploitSearchTerm.trim() ? 'No exploits found matching your search' : 'No exploits found'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-6">
                      {exploits.map((exploit) => (
                        <div
                          key={exploit.id}
                          className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 hover:shadow-xl transition-all"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100 mb-2">
                                {exploit.title}
                              </h3>
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                  exploit.severity === 'CRITICAL' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                  exploit.severity === 'HIGH' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                  exploit.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-blue-200'
                                }`}>
                                  {exploit.severity}
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {exploit.source}
                                </span>
                                {exploit.language === 'english' && (
                                  <span className="text-xs text-green-600 dark:text-green-400">
                                    ✓ English
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                            {exploit.description}
                          </p>
                          
                          {exploit.task_types && exploit.task_types.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {exploit.task_types.map((type, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-xs"
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {!exploitSearchTerm.trim() && exploits.length > 0 && (
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setExploitsPage(p => Math.max(1, p - 1))}
                          disabled={exploitsPage === 1}
                          className="px-4 py-2 bg-slate-200 dark:bg-slate-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Page {exploitsPage}
                        </span>
                        <button
                          onClick={() => setExploitsPage(p => p + 1)}
                          disabled={exploits.length < 50}
                          className="px-4 py-2 bg-slate-200 dark:bg-slate-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

export default OffensiveKnowledgeBase;
